// Platform Admin → Client compliance review.
//
// The only place a rental business's evidence can become "ConfirmPH verified".
// "Verify" means ConfirmPH reviewed the document. "Authority confirmed" needs a
// reference to the issuing authority's own independent confirmation.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, BookOpen, Inbox } from "lucide-react";

import { api } from "../../lib/api";
import { ccGet, ccPost, errorMessage, humanise, statusLabel } from "../../lib/clientLegal";
import {
  Button, Empty, Field, Loading, Modal, Note, Section, Tabs, inputClass,
} from "../../components/platform-legal/ui";
import { Guide, StatusPill } from "../../components/client-legal/RequirementDetail";

const TABS = [
  { key: "queue", label: "Evidence queue", icon: Inbox },
  { key: "reports", label: "Reports", icon: BarChart3 },
  { key: "catalogue", label: "Legal catalogue", icon: BookOpen },
];
const QUEUE_LABELS = {
  new_evidence: "New evidence", authority_review: "Authority review", more_information: "Waiting on business",
  not_applicable_claims: "Not-applicable claims", expiring: "Expiring", high_risk: "High risk", all_open: "All open",
};
const ACTION_LABELS = {
  VERIFY: "Verify evidence (ConfirmPH reviewed)",
  VERIFY_AUTHORITY_CONFIRMED: "Verify: authority independently confirmed",
  REJECT: "Reject", REQUEST_MORE_INFORMATION: "Request more information",
  MARK_NEEDS_AGENCY_CONFIRMATION: "Needs agency confirmation", MARK_NOT_APPLICABLE: "Accept not applicable",
  REJECT_NOT_APPLICABLE: "Reject not-applicable claim", REVOKE_VERIFICATION: "Revoke verification",
  CONFIRM_IDENTITY_MISMATCH: "Confirm identity mismatch", TEMPORARY_PLATFORM_DECISION: "Temporary platform decision",
};

export default function ClientCompliance() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  useEffect(() => { ccGet("/status").then(setStatus).catch((err) => toast.error(errorMessage(err))); }, []);
  if (!status) return <Loading />;
  if (!status.has_access) {
    return (
      <div className="space-y-4" data-testid="client-compliance-page">
        <h1 className="font-display text-2xl font-bold">Client compliance review</h1>
        <Section><Empty testId="cc-no-access">You don't have client compliance review access. Platform admin alone is not enough.</Empty></Section>
      </div>
    );
  }
  const tab = params.get("tab") || "queue";
  return (
    <div className="space-y-4" data-testid="client-compliance-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Client compliance review</h1>
        <p className="text-sm text-slate-600 mt-1">Review rental businesses' legal evidence. ConfirmPH verifies what it
          actually checked; it is not LTO, LTFRB, BIR, SEC, DTI, DOT or an LGU.</p>
      </div>
      <Tabs tabs={TABS} active={tab} onChange={(k) => setParams({ tab: k })} testId="cc-tabs" />
      {tab === "queue" && <Queue status={status} />}
      {tab === "reports" && <Reports kinds={status.reports} />}
      {tab === "catalogue" && <Catalogue canAdmin={status.capabilities.includes("client_compliance.admin")} />}
    </div>
  );
}

function Queue({ status }) {
  const [filters, setFilters] = useState({ tab: "new_evidence", q: "", critical_only: false, vehicles_only: false, page: 1 });
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => ccGet("/queue", { params: { ...filters, page_size: 20 } })
    .then(setData).catch((err) => toast.error(errorMessage(err))), [filters]);
  useEffect(() => { load(); }, [load]);
  return (
    <Section title="Evidence queue" testId="cc-queue">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <select className={inputClass} value={filters.tab} aria-label="Queue"
                onChange={(e) => setFilters({ ...filters, tab: e.target.value, page: 1 })} data-testid="cc-queue-tab">
          {status.queues.map((q) => <option key={q} value={q}>{QUEUE_LABELS[q] || q}</option>)}
        </select>
        <input className={inputClass} placeholder="Business name" value={filters.q} aria-label="Business name"
               onChange={(e) => setFilters({ ...filters, q: e.target.value, page: 1 })} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={filters.critical_only}
               onChange={(e) => setFilters({ ...filters, critical_only: e.target.checked, page: 1 })} /> Critical only</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={filters.vehicles_only}
               onChange={(e) => setFilters({ ...filters, vehicles_only: e.target.checked, page: 1 })} /> Vehicles only</label>
      </div>
      {!data ? <Loading /> : data.items.length === 0 ? <Empty>No requirements need review in this queue.</Empty> : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: 560 }} data-testid="cc-queue-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-500 border-b">
              <th className="py-2 pr-3">Business</th><th className="py-2 pr-3">Requirement</th>
              <th className="py-2 pr-3">Submitted</th><th className="py-2 pr-3">Evidence</th><th className="py-2 pr-3" />
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((r) => (
                <tr key={r.id}>
                  <td className="py-2 pr-3">{r.company_name || "—"}</td>
                  <td className="py-2 pr-3">{r.title}{r.queue?.vehicle_label ? ` · ${r.queue.vehicle_label}` : ""}
                    {r.criticality === "CRITICAL_BLOCKER" && <span className="ml-1 text-xs text-rose-700">(critical)</span>}</td>
                  <td className="py-2 pr-3 text-xs">{r.review_requested_at?.slice(0, 10) || "—"}</td>
                  <td className="py-2 pr-3 text-xs">{r.evidence_count}</td>
                  <td className="py-2 pr-3"><Button variant="secondary" onClick={() => setOpen(r.id)} data-testid={`cc-open-${r.id}`}>Review</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && (
        <div className="mt-2 flex items-center justify-between text-xs">
          <Button variant="secondary" disabled={filters.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })}>Previous</Button>
          <span>{data.total} total</span>
          <Button variant="secondary" disabled={filters.page * 20 >= data.total} onClick={() => setFilters({ ...filters, page: filters.page + 1 })}>Next</Button>
        </div>
      )}
      {open && <ReviewModal recordId={open} status={status} onClose={() => setOpen(null)} onDone={() => { setOpen(null); load(); }} />}
    </Section>
  );
}

function ReviewModal({ recordId, status, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ action: "VERIFY", reason: "", business_message: "", internal_note: "",
    expiry_date: "", issue_date: "", no_expiry: false, authority_reference: "", outcome: "NEEDS_AUTHORITY_REVIEW",
    decision_expires_at: "", legal_source: "", decision_scope: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { ccGet(`/records/${recordId}`).then(setData).catch((err) => toast.error(errorMessage(err))); }, [recordId]);
  const canEvidence = status.capabilities.includes("client_compliance.evidence.view");
  const canAdmin = status.capabilities.includes("client_compliance.admin");

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await ccPost(`/records/${recordId}/decision`, payload);
      toast.success("Decision recorded.");
      onDone();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const openEvidence = async (id) => {
    try {
      const res = await api.get(`/admin/client-compliance/evidence/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  if (!data) return <Modal title="Loading" onClose={onClose}><Loading /></Modal>;
  const { record, evaluation, definition, evidence, history, company, vehicle } = data;
  const actions = status.actions.filter((a) => a !== "TEMPORARY_PLATFORM_DECISION" || canAdmin);
  return (
    <Modal title={definition.title} description={`${company?.name || ""}${vehicle ? ` · ${vehicle.name}` : ""}`}
           onClose={onClose} testId="cc-review-modal">
      <div className="flex flex-wrap gap-2 items-center">
        <StatusPill status={evaluation.status} />
        <span className="text-xs text-slate-600">Applicability: {data.applicability.reason}</span>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
        <div><dt className="text-xs text-slate-500">Claimed reference</dt><dd>{record.reference_number || "—"}</dd></div>
        <div><dt className="text-xs text-slate-500">Claimed expiry</dt><dd>{record.expiry_date || "—"}</dd></div>
        <div><dt className="text-xs text-slate-500">Previous verification</dt>
          <dd>{record.verified ? `${record.verified.level} ${record.verified.at?.slice(0, 10)}${record.verified.revoked ? " (revoked)" : ""}` : "None"}</dd></div>
        <div><dt className="text-xs text-slate-500">Rule version</dt><dd>{definition.version}</dd></div>
      </dl>
      <section>
        <h4 className="text-sm font-semibold">Evidence</h4>
        {evidence.length === 0 ? <Empty>No evidence.</Empty> : (
          <ul className="space-y-1 text-xs">
            {evidence.map((e) => (
              <li key={e.id} className="flex flex-wrap justify-between gap-2 rounded border border-slate-200 px-2 py-1">
                <span>v{e.version} · {humanise(e.document_type)} · {e.status}{e.possible_duplicate_of ? " · possible duplicate" : ""}</span>
                {canEvidence && <button type="button" className="underline" onClick={() => openEvidence(e.id)}>Open (logged)</button>}
              </li>
            ))}
          </ul>
        )}
      </section>
      <Guide guide={definition} />
      <section className="space-y-2 rounded-xl border border-slate-200 p-3" data-testid="cc-decision-form">
        <Field label="Decision">
          <select className={inputClass} value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })}
                  data-testid="cc-action">
            {actions.map((a) => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
          </select>
        </Field>
        {form.action.startsWith("VERIFY") && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label="Issue date (from document)"><input type="date" className={inputClass} value={form.issue_date}
                   onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></Field>
            <Field label="Expiry date (from document)"><input type="date" className={inputClass} value={form.expiry_date}
                   onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="cc-expiry" /></Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.no_expiry}
                   onChange={(e) => setForm({ ...form, no_expiry: e.target.checked })} /> Document has no expiry</label>
          </div>
        )}
        {form.action === "VERIFY_AUTHORITY_CONFIRMED" && (
          <Field label="Authority confirmation reference" hint="The issuing authority's own independent confirmation.">
            <input className={inputClass} value={form.authority_reference}
                   onChange={(e) => setForm({ ...form, authority_reference: e.target.value })} />
          </Field>
        )}
        {form.action === "TEMPORARY_PLATFORM_DECISION" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label="Outcome">
              <select className={inputClass} value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })}>
                {["NEEDS_AUTHORITY_REVIEW", "REVIEW_REQUIRED", "NOT_APPLICABLE"].map((o) => <option key={o} value={o}>{statusLabel(o)}</option>)}
              </select>
            </Field>
            <Field label="Expires"><input type="date" className={inputClass} value={form.decision_expires_at}
                   onChange={(e) => setForm({ ...form, decision_expires_at: e.target.value })} /></Field>
            <Field label="Legal source"><input className={inputClass} value={form.legal_source}
                   onChange={(e) => setForm({ ...form, legal_source: e.target.value })} /></Field>
            <Field label="Scope"><input className={inputClass} value={form.decision_scope}
                   onChange={(e) => setForm({ ...form, decision_scope: e.target.value })} /></Field>
          </div>
        )}
        <Field label="Reason (required)"><textarea className={inputClass} rows={2} value={form.reason}
               onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="cc-reason" /></Field>
        <Field label="Message to the business"><textarea className={inputClass} rows={2} value={form.business_message}
               onChange={(e) => setForm({ ...form, business_message: e.target.value })} /></Field>
        <Field label="Internal note (never shown to the business)"><textarea className={inputClass} rows={2}
               value={form.internal_note} onChange={(e) => setForm({ ...form, internal_note: e.target.value })} /></Field>
        <Note tone="slate">"Verify" records that ConfirmPH reviewed this evidence. It is not a government certification.</Note>
        <div className="flex justify-end"><Button busy={busy} onClick={submit} data-testid="cc-submit">Record decision</Button></div>
      </section>
      <section>
        <h4 className="text-sm font-semibold">History</h4>
        <ol className="space-y-1 text-xs text-slate-700">
          {history.map((h) => <li key={h.id}>{h.at?.slice(0, 16).replace("T", " ")} · {h.summary}
            {h.internal_note ? ` · internal: ${h.internal_note}` : ""}</li>)}
        </ol>
      </section>
    </Modal>
  );
}

function Reports({ kinds }) {
  const [kind, setKind] = useState("critical_blocks");
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); ccGet(`/reports/${kind}`).then(setData).catch((err) => toast.error(errorMessage(err))); }, [kind]);
  return (
    <Section title="Reports" actions={
      <select className={inputClass} value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Report" data-testid="cc-report-kind">
        {kinds.map((k) => <option key={k} value={k}>{humanise(k)}</option>)}
      </select>}>
      {!data ? <Loading /> : data.counts ? (
        <ul className="text-sm" data-testid="cc-report">{Object.entries(data.counts).map(([k, v]) => <li key={k}>{humanise(k)}: {v}</li>)}</ul>
      ) : (data.items || []).length === 0 ? <Empty>Nothing in this report.</Empty> : (
        <ul className="space-y-1 text-sm" data-testid="cc-report">
          {data.items.map((r, i) => (
            <li key={r.id || r.key || r.company_id || i} className="rounded border border-slate-200 px-2 py-1">
              {r.company_name || r.title || r.key} {r.title && r.company_name ? `· ${r.title}` : ""}
              {r.verified?.expiry_date ? ` · expiry ${r.verified.expiry_date}` : ""}
              {r.review_after ? ` · review after ${r.review_after}` : ""}
              {r.pending_review !== undefined ? ` · ${r.pending_review} pending, ${r.verified} verified` : ""}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function Catalogue({ canAdmin }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => ccGet("/catalogue").then(setData).catch((err) => toast.error(errorMessage(err))), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  return (
    <Section title={`Legal catalogue ${data.version}`} description="Guidance updates create a new version. Historical verifications are never rewritten.">
      <ul className="space-y-1 text-sm" data-testid="cc-catalogue">
        {data.items.map((g) => (
          <li key={g.key} className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-200 px-2 py-1">
            <span>{g.title} <span className="text-xs text-slate-500">v{g.version} · {g.freshness.toLowerCase().replace(/_/g, " ")}</span></span>
            {canAdmin && <Button variant="ghost" onClick={() => setEditing(g)}>Publish update</Button>}
          </li>
        ))}
      </ul>
      {editing && <CatalogueUpdate guide={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </Section>
  );
}

function CatalogueUpdate({ guide, onClose, onDone }) {
  const [form, setForm] = useState({ short_description: guide.short_description, change_summary: "", review_after: "", reverify: false });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const overrides = { short_description: form.short_description };
      if (form.review_after) overrides.review_after = form.review_after;
      await ccPost(`/catalogue/${guide.key}/versions`, { change_summary: form.change_summary, overrides,
        require_reverification: form.reverify });
      toast.success("New catalogue version published.");
      onDone();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Update guidance: ${guide.title}`} onClose={onClose}>
      <Field label="Short description"><textarea className={inputClass} rows={2} value={form.short_description}
             onChange={(e) => setForm({ ...form, short_description: e.target.value })} /></Field>
      <Field label="Next source review"><input type="date" className={inputClass} value={form.review_after}
             onChange={(e) => setForm({ ...form, review_after: e.target.value })} /></Field>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.reverify}
             onChange={(e) => setForm({ ...form, reverify: e.target.checked })} /> Existing verifications need reassessment</label>
      <Field label="What changed and why"><textarea className={inputClass} rows={2} value={form.change_summary}
             onChange={(e) => setForm({ ...form, change_summary: e.target.value })} /></Field>
      <div className="flex justify-end"><Button busy={busy} onClick={submit}>Publish version</Button></div>
    </Modal>
  );
}
